import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/home.css';
import { fetchAllDatasetJson } from '../services/datasetService';

const HomePage = () => {
  const [stats, setStats] = useState({
    records: 0,
    polymers: 0,
    solvents: 0,
    articles: 0,
    loading: true
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await fetchAllDatasetJson();
        if (data && Array.isArray(data)) {
          const polymers = new Set();
          const solvents = new Set();
          const articles = new Set();

          data.forEach(record => {
            // Collect Polymers
            record.polymerProperty?.polymerComponents?.forEach(p => {
              if (p.polymerName) polymers.add(p.polymerName.trim().toLowerCase());
            });
            // Collect Solvents
            record.solventProperty?.solventComponents?.forEach(s => {
              if (s.solventName) solvents.add(s.solventName.trim().toLowerCase());
            });
            // Collect Articles (Unique DOIs)
            if (record.researchMetadata?.doi && record.researchMetadata.doi.trim()) {
              articles.add(record.researchMetadata.doi.trim().toLowerCase());
            }
          });

          setStats({
            records: data.length,
            polymers: polymers.size,
            solvents: solvents.size,
            articles: articles.size,
            loading: false
          });
        }
      } catch (error) {
        console.error("Failed to fetch dataset for home page stats:", error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };
    loadStats();
  }, []);

  return (
    <div className="home-wrapper">
      {/* Hero Section - Split Layout */}
      <section className="hero-section d-flex align-items-center">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 text-center text-lg-start">
              <h1 className="hero-heading">
                Electrospinning <br />
                <span className="text-primary">Data Hub</span>
              </h1>
              <p className="hero-subheading mt-3 mb-4">
                A shared platform for collecting and exploring electrospinning datasets.        </p>
              <p className="hero-description mb-4">
                Researchers can contribute experimental data in a structured format, making it easier to compare studies, analyze trends, and support data-driven research in electrospinning.
                By bringing datasets from different publications into one place, the platform helps the community work with consistent, reusable data.              </p>
              <div className="d-flex gap-3 justify-content-center justify-content-lg-start">
                <Link to="/dataSubmission" className="btn btn-primary btn-lg px-4">Contribute Data</Link>
                <Link to="/dataset" className="btn btn-outline-secondary btn-lg px-4">Explore Data</Link>
              </div>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0 text-center">
              <img
                src="/assets/images/electrospinning-setup.jpg"
                alt="Electrospinning Setup"
                className="hero-image img-fluid"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Database Highlights Section */}
      <section className="highlights-section py-4 border-bottom">
        <div className="container">
          <div className="row text-center g-4">
            <div className="col-6 col-md-3">
              <div className="highlight-item">
                <span className="highlight-value">{stats.loading ? '...' : stats.records.toLocaleString()}</span>
                <span className="highlight-label">Records</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="highlight-item">
                <span className="highlight-value">{stats.loading ? '...' : stats.polymers.toLocaleString()}</span>
                <span className="highlight-label">Polymers</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="highlight-item">
                <span className="highlight-value">{stats.loading ? '...' : stats.solvents.toLocaleString()}</span>
                <span className="highlight-label">Solvents</span>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="highlight-item">
                <span className="highlight-value">{stats.loading ? '...' : stats.articles.toLocaleString()}</span>
                <span className="highlight-label">Articles</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="section-title">Why Contribute?</h2>
            <div className="section-divider mx-auto"></div>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <h4>Open Data</h4>
                <p>Datasets can be downloaded and reused by other researchers. Making experimental data accessible supports transparency and allows results to be reanalyzed or compared with new studies.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <h4>Support Comparative Research</h4>
                <p>When experimental parameters and results are reported in a structured format, it becomes easier to compare outcomes across different studies and materials.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <h4>Improve Data Quality</h4>
                <p>Contributing data helps build a consistent dataset that can be used for statistical analysis, modeling, and future data-driven approaches in electrospinning research.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section from redesign requirements, simplified */}
      <section className="cta-section text-center">
        <div className="container">
          <div className="cta-content mx-auto" style={{ maxWidth: '600px' }}>
            <h2 className="text-white mb-2 fs-3">Contribute Data</h2>
            <p className="text-white-50 mb-3 fs-6">Share your electrospinning dataset to support open, structured, and reusable research data.</p>
            <Link to="/dataSubmission" className="btn btn-light btn-md px-4 fw-bold text-primary">Contribute Data</Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
